import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { FilterSidebar, type FilterGroup } from "@/components/filter-sidebar";
import { ListToolbar } from "@/components/list-toolbar";
import { PaginationBar } from "@/components/pagination-bar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

type Filter = "all" | "active" | "inactive" | "lowStock";

export default function ProductsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [filter, setFilter] = useState<Filter>("all");
  const [filtersOpen, setFiltersOpen] = useState(true);

  const params = useMemo(() => {
    if (filter === "active")   return { isActive: true };
    if (filter === "inactive") return { isActive: false };
    return {};
  }, [filter]);

  const { data, isLoading } = useList<any>("/products", { q, page, pageSize, sortBy, sortDir, ...params });
  const all = data?.data ?? [];
  const rows = filter === "lowStock"
    ? all.filter((p: any) => p.reorderLevel != null && p.quantityInStock != null && p.quantityInStock <= p.reorderLevel)
    : all;
  const total = data?.pagination?.total;

  async function del(id: string, name: string) {
    if (!confirm(`Delete product "${name}"?`)) return;
    await api.delete(`/products/${id}`);
    qc.invalidateQueries({ queryKey: ["/products"] });
  }

  const groups: FilterGroup[] = [
    {
      title: "Status",
      items: [
        { label: "All IT products", active: filter === "all",    onClick: () => setFilter("all"),      count: total },
        { label: "Active",       active: filter === "active",   onClick: () => setFilter("active") },
        { label: "Inactive",     active: filter === "inactive", onClick: () => setFilter("inactive") },
        { label: "Low stock",    active: filter === "lowStock", onClick: () => setFilter("lowStock") },
      ],
    },
  ];

  return (
    <>
      <PageHeader
        title="IT Products"
        description="Catalog of IT products — licenses, SaaS, hardware, services."
        actions={<Button asChild><Link to="/products/new"><Plus className="mr-1 h-4 w-4"/>Create IT Product</Link></Button>}
      />

      <ListToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        sortBy={sortBy}
        sortDir={sortDir}
        sortOptions={[
          { value: "createdAt", label: "Created" },
          { value: "name",      label: "Name" },
          { value: "unitPrice", label: "Unit price" },
        ]}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        rightSlot={<Input placeholder="Search IT products…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-8 w-64" />}
      />

      <div className="flex flex-1 min-h-[60vh]">
        {filtersOpen && <FilterSidebar groups={groups} onClear={() => setFilter("all")} />}

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex-1 overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Category</Th>
                  <Th className="text-right">Unit Price</Th>
                  <Th>Unit</Th>
                  <Th>Manufacturer</Th>
                  <Th className="text-right">Stock</Th>
                  <Th>Active</Th>
                  <Th className="w-10"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={9} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && rows.length === 0 && (
                  <Tr><Td colSpan={9} className="py-12 text-center text-muted-foreground">No IT products yet.</Td></Tr>
                )}
                {rows.map((p: any) => {
                  const low = p.reorderLevel != null && p.quantityInStock != null && p.quantityInStock <= p.reorderLevel;
                  return (
                    <Tr key={p.id} className="group">
                      <Td><Link to={`/products/${p.id}`} className="font-medium hover:underline">{p.name}</Link></Td>
                      <Td className="font-mono text-xs text-muted-foreground">{p.code ?? "—"}</Td>
                      <Td>{p.category ?? "—"}</Td>
                      <Td className="text-right tabular-nums">
                        {p.unitPrice != null ? `${p.currency ?? "USD"} ${Number(p.unitPrice).toLocaleString()}` : "—"}
                      </Td>
                      <Td className="text-muted-foreground">{p.usageUnit ?? "—"}</Td>
                      <Td className="text-muted-foreground">{p.manufacturer ?? "—"}</Td>
                      <Td className="text-right tabular-nums">
                        {p.quantityInStock != null ? p.quantityInStock : "—"}
                        {low && <Badge variant="destructive" className="ml-1 text-[10px]">Low</Badge>}
                      </Td>
                      <Td>{p.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</Td>
                      <Td>
                        <Button variant="ghost" size="icon" onClick={() => del(p.id, p.name)} aria-label="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </div>

          <PaginationBar
            page={page} pageSize={pageSize} total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>
    </>
  );
}
